import { useNotifyManager } from '@0x234/common-vue/composables/notify-manager';
import axios from 'axios';
import { boot } from 'quasar/wrappers';
import { Route } from 'router/routes';
import env from 'src/configs/env';
import { useAppStore } from 'stores/app-store';
import { useAuthStore } from 'stores/auth-store';

export default boot(async ({ app, ssrContext, router, store }) => {
    const defaultTimeout = ssrContext ? 6000 : 15000;
    const siteBasicAuthHeader = `Basic ${btoa([env.siteUsername, env.sitePassword].join(':'))}`;
    const [authClient, apiClient] = [
        axios.create({
            baseURL: env.authServerEndpoint,
            withCredentials: true,
            timeout: defaultTimeout,
            headers: {
                'Authorization': siteBasicAuthHeader
            }
        }),
        axios.create({
            baseURL: env.apiServerEndpoint,
            withCredentials: true,
            timeout: defaultTimeout
        })
    ];
    if (isAppRunningInDevMode()) {
        await permitUnsignedSSLCertificates(apiClient, authClient);
    }
    if (isAppRunningInSSRContext(ssrContext)) {
        configureSSRClientHeaders(ssrContext, apiClient, authClient);
        configureSSRClientInterceptors(ssrContext, apiClient, authClient);
    }
    configureClientUnavailableInterceptor(router, apiClient, authClient);
    configureClientUnauthorizedInterceptor(app, router, apiClient, authClient);
    injectClientsToApp(app, { $api: apiClient, $auth: authClient });
    injectClientsToPinia(store, { $api: apiClient, $auth: authClient });
});

function isAppRunningInSSRContext(ssrContext) {
    return !process.env.CLIENT && ssrContext;
}

function isAppRunningInDevMode() {
    return !process.env.CLIENT && process.env.DEV;
}

async function permitUnsignedSSLCertificates(...clients) {
    const https = await import('https');
    const httpsAgent = new https.Agent({ rejectUnauthorized: false });
    for (const client of clients) {
        client.defaults.httpsAgent = httpsAgent;
    }
}

function configureSSRClientHeaders(ssrContext, ...clients) {
    const clientUserAgent = ssrContext.req.headers['user-agent'];
    const clientIPAddress = ssrContext.req.headers['x-forwarded-for'] || ssrContext.req.ip;
    const cookies = ssrContext.req.headers.cookie ?? '';
    for (const client of clients) {
        if (clientUserAgent) client.defaults.headers.common['User-Agent'] = clientUserAgent;
        if (clientIPAddress) client.defaults.headers.common['X-Forwarded-For'] = clientIPAddress;
        if (cookies) client.defaults.headers.common['Cookie'] = cookies;
    }
}

function configureSSRClientInterceptors(ssrContext, ...clients) {
    for (const client of clients) {
        client.interceptors.response.use(response => {
            if (response.headers['set-cookie']) {
                const setCookieHeaders = response.headers['set-cookie'];
                setCookieHeaders.forEach(cookie => {
                    ssrContext.res.append('Set-Cookie', cookie);
                });
            }
            return response;
        });
    }
}

function configureClientUnavailableInterceptor(router, ...clients) {
    for (const client of clients) {
        client.interceptors.response.use(
            res => {
                return res;
            },
            err => {
                // Handle specific server error status codes
                if (err?.response?.status && [502, 503, 504, 521].includes(err.response.status)) {
                    useAppStore().unavailableServiceDialog = { type: 'server' };
                }
                // Handle network errors, CORS failures, and timeouts
                else if (
                    err.code === 'ERR_NETWORK'
                    || err.message === 'Network Error'
                    || err.code === 'ECONNABORTED'
                    || (err.message && (err.message.includes('CORS') || err.message.includes('cross-origin')))
                ) {
                    useAppStore().unavailableServiceDialog = { type: 'server' };
                }
                return Promise.reject(err);
            }
        );
    }
}

function configureClientUnauthorizedInterceptor(app, router, ...clients) {
    for (const client of clients) {
        client.interceptors.response.use(
            res => {
                return res;
            },
            err => {
                if (err.response?.status === 401 && useAuthStore().isUserAuthenticated) {
                    app.config.globalProperties.$event.dispatchEvent(new Event('session:expired'));
                    useNotifyManager().notifyError('You have been logged out.');
                    router.push({ name: Route.LoginPage });
                } else if (err.response?.status === 401) {
                    app.config.globalProperties.$event.dispatchEvent(new Event('session:unauthorized'));
                }
                return Promise.reject(err);
            }
        );
    }
}

function injectClientsToApp(app, clients = {}) {
    app.config.globalProperties.$axios = axios;
    for (const [k, v] of Object.entries(clients)) {
        app.provide(k, v);
        app.config.globalProperties[k] = v;
    }
}

function injectClientsToPinia(store, clients = {}) {
    store.use(() => clients); // Only applicable to stores which have not yet been initialized.
    for (const [k, v] of Object.entries(clients)) {
        store[k] = v;
    }
    for (const state of Object.keys(store.state.value)) {
        const childStore = store._s.get(state);
        if (!childStore) continue;
        for (const k of Object.keys(clients)) {
            childStore[k] = clients[k];
        }
    }
}
